import { Collections } from "@diacare/common/models/collections";
import { uploadFileEntry } from "@diacare/common/utils/upload-file-entry";
import { DataAccessProvider, FileContext } from "@litbase/admin";
import { defaultIsGranted, RestrictedProvider } from "@litbase/alexandria";
import { client } from "@litbase/client";
import { BinaryId, createBinaryId, DocumentInterface, emptyUser, isEmptyUser, isUserGuest, litql } from "@litbase/core";
import { ReactNode } from "react";
import { modelConfigs } from "../models";

export function LitbaseDataProvider({ children }: { children: ReactNode }) {
  return (
    <DataAccessProvider<DocumentInterface, BinaryId>
      fetchBatch={async (args) => {
        return ((await litql.query(args.collection, {
          $matchAll: {},
          $sort: args.orderings,
          ...(args.skip ? { $skip: args.skip } : {}),
          ...(args.limit ? { $limit: args.limit } : {}),
          ...modelConfigs[args.collection as keyof typeof modelConfigs]?.queryFields,
        })) || []) as DocumentInterface[];
      }}
      fetchSingle={(collection, _id) => litql.query(collection, { $match: { _id } }) as Promise<DocumentInterface>}
      getId={(elem) => elem._id}
      persist={async (collection, item) => {
        if (collection === Collections.USERS) return;
        // React-hook-form tends to FUCK UP BinaryIds so we have to do things we would rather not.
        const transformedItem = Object.fromEntries(
          Object.entries(item as Record<string, { value?: string }>).map(([key, value]) => [
            key,
            typeof value?.value === "string" && Object.keys(value).length === 1 ? new BinaryId(value.value) : value,
          ]),
        );

        const { _id, ...elem } = transformedItem as unknown as DocumentInterface;
        const id = _id || createBinaryId();
        await litql.query(collection, {
          $match: { _id: id },
          $upsert: { $setOnInsert: { _id: id }, $set: elem },
        });
      }}
      delete={async (collection, id) => {
        await litql.query(collection, { $match: { _id: id }, $pull: 1 });
      }}
      stringToId={(string) => (string === "new" ? createBinaryId() : BinaryId.fromUrlSafeString(string))}
      idToString={(id) => id.toUrlSafeString()}
    >
      <RestrictedProvider
        user$={client.currentUser$}
        defaultUser={emptyUser}
        isEmptyUser={isEmptyUser}
        isGuestUser={isUserGuest}
        isGranted={defaultIsGranted}
      >
        <FileContext.Provider value={{ uploadFile: uploadFileEntry }}>{children}</FileContext.Provider>
      </RestrictedProvider>
    </DataAccessProvider>
  );
}
